import _ from "lodash";
import React from "react";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import {
  typesConfig,
  getSearchFilterDataMemoized,
  getParseResultMemoized,
  getModuleTypeBySearchTerm,
  getIsAllCurriculumSelected,
  getCourseBasedModuleMemoized,
  getIsInsightsSearchV2Enabled,
} from "../Utils";
import { getGlobalSearchResult } from "../../modules/GlobalSearchBarQueries";
import { getGlobalSearchResultFromCache } from "../../modules/GlobalSearchBarHelper";
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import { SearchList } from "../SearchList/SearchList";
import classes from "./Suggestions.scss";
import classNames from "classnames";
import {
  getCurrentCurriculumContextForGlobalSearch,
  getUserCurriculumPrograms,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import {
  AddStudentIllustration,
  NoClassesIllustration,
} from "@toddle-edu/ds-theme";

const getEmptyText = ({ module, t }) => {
  const moduleTitle = t(_.get(typesConfig, [module, "title"], ""));

  switch (module) {
    case typesConfig.STUDENT_PROFILE.id:
    case typesConfig.TEACHER_PROFILE.id:
    case typesConfig.FAMILY_PROFILE.id:
      return t("no_suggestions_for_user_modules", { module: moduleTitle });
    default:
      return t("no_suggestions_for_other_modules", { module: moduleTitle });
  }
};

const getEmptyImageUrl = ({ module }) => {
  switch (module) {
    case typesConfig.STUDENT_PROFILE.id:
    case typesConfig.TEACHER_PROFILE.id:
    case typesConfig.FAMILY_PROFILE.id:
      return AddStudentIllustration;
    default:
      return NoClassesIllustration;
  }
};

const NoResultsComponent = props => {
  const { module, t } = props;
  return (
    <div className={classes.emptyResult}>
      <div className={classes.imageContainer}>
        <img
          className={classes.image}
          src={getEmptyImageUrl({ module })}
          alt={t("common:image_not_found")}
        />
      </div>
      <div className="text-subtitle-3">{getEmptyText({ module, t })}</div>
    </div>
  );
};

const Suggestions = props => {
  const {
    module,
    suggestions = [],
    suggestionModules,
    t,
    onSuggestionClick,
  } = props;

  const data =
    module === typesConfig.MODULES.id ||
    (module === typesConfig.PLANNING_INSIGHTS_MODULE.id &&
      getIsInsightsSearchV2Enabled())
      ? suggestionModules
      : suggestions;

  return (
    <div className={classes.container} style={{ minHeight: 0 }}>
      {_.isEmpty(data) ? (
        <NoResultsComponent module={module} t={t} />
      ) : (
        <>
          <div
            className={classNames(
              "text-body text-textSubtle",
              classes.headingWrapper
            )}
          >
            {t("common:suggestions_for_you")}
          </div>
          <div className={classes.listContainer}>
            <SearchList
              data={_.slice(data, 0, 4)}
              onSelectItem={onSuggestionClick}
            />
          </div>
        </>
      )}
    </div>
  );
};

const getFilteredInsightsModules = ({ insightBasedModules, curriculumIds }) => {
  return _.filter(insightBasedModules, module =>
    _.includes(curriculumIds, module.metadata.curriculum)
  );
};

const getFilteredInsightsModulesMemoized = _.memoize(
  getFilteredInsightsModules,
  JSON.stringify
);

const mapStateToProps = (state, ownProps) => {
  const { module, t, courses } = ownProps;

  const {
    id: currentCurriculumProgramId,
    type: currentCurriculumProgramType,
  } = getCurrentCurriculumContextForGlobalSearch(state);

  const userCurriculumPrograms = getUserCurriculumPrograms(state);

  const userCurriculumProgramType = _.map(
    userCurriculumPrograms,
    curriculum => curriculum.type
  );

  const userCurriculumProgramsIds = _.map(userCurriculumPrograms, "id");

  const isAllCurriculumSelected = getIsAllCurriculumSelected(
    currentCurriculumProgramId
  );

  const curriculumProgramType = isAllCurriculumSelected
    ? userCurriculumProgramType
    : [currentCurriculumProgramType];

  const curriculumIds = isAllCurriculumSelected
    ? userCurriculumProgramsIds
    : [currentCurriculumProgramId];

  const academicYearSelectedId = getSelectedAcademicYearId(state);

  const { userInfo } = state.login;

  const suggestionModules =
    module === typesConfig.MODULES.id
      ? getModuleTypeBySearchTerm({
          term: "",
          orgId: userInfo.org_id,
          module,
          curriculumProgramType,
          userInfo,
          shouldIncludeOnlyAdminModules: true,
          t,
        })
      : module === typesConfig.PLANNING_INSIGHTS_MODULE.id &&
        getIsInsightsSearchV2Enabled()
      ? getFilteredInsightsModulesMemoized({
          insightBasedModules: getCourseBasedModuleMemoized({
            courses,
            orgId: userInfo.org_id,
            userInfo,
            isInsightsBasedModules: true,
            t,
          }),
          curriculumIds,
        })
      : [];

  const filters = getSearchFilterDataMemoized({
    academicYearSelectedId,
    curriculumIds,
    searchText: "",
    module,
  });

  const isInvalidCurriculumId = _.some(curriculumIds, _.isNil);

  return {
    isData: true,
    isLoading: false,
    filters,
    suggestionModules: getParseResultMemoized({ result: suggestionModules, t }),
    isInvalidCurriculumId,
  };
};

export default compose(
  I18nHOC({
    resource: [
      "common",
      "teacherHomePage",
      "classRoom",
      "configuration",
      "attendance",
      "integration",
    ],
  }),
  connect(mapStateToProps, null),
  graphql(getGlobalSearchResult, {
    name: "getGlobalSearchResultQuery",
    options: ({ filters }) => ({
      fetchPolicy: "cache-and-network",
      variables: filters,
    }),
    skip: props => {
      const { module, isInvalidCurriculumId } = props;
      return (
        module == typesConfig.MODULES.id ||
        isInvalidCurriculumId ||
        (module === typesConfig.PLANNING_INSIGHTS_MODULE.id &&
          getIsInsightsSearchV2Enabled())
      );
    },
    props: ({
      getGlobalSearchResultQuery,
      ownProps: { isData, isLoading, filters, t },
    }) => {
      const result = getGlobalSearchResultFromCache({
        filters,
      });

      const queryResult = _.get(result, "platform.platformSearch", []);

      const suggestions = getParseResultMemoized({
        result: queryResult,
        t,
      });

      return {
        suggestions,
        isData: !_.isEmpty(result) && isData,
        isLoading:
          _.includes([1, 2], getGlobalSearchResultQuery["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  withLoader
)(Suggestions);
